import { ALERT_LEVEL_CONFIG } from '@/views/commandCenter/config'
import {
  buildMonitoringStreamKeyFromDevice,
  getMonitoringAlarmRecordId,
  getMonitoringFullImageUrl,
  getMonitoringIsProcessed,
  getMonitoringSceneFlags,
  getMonitoringThumbnailUrl,
  getSeeData,
  getStreamKey
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'

/** 与格子 DEPARTURE_TYPE 同一套线值，redux 不从页面组件引 */
const DEPARTURE_RETURN_ROOM = 'RETURN_ROOM'
const DEPARTURE_NURSING = 'NURSING'

/** 点送回后未见宝宝的空档 */
export const RETURN_AWAITING_GRACE_MS = 10 * 60 * 1000
export const RETURN_AWAITING_GRACE_MINUTES = Math.round(RETURN_AWAITING_GRACE_MS / 60000)

export function isReturnAwaitingGraceExpired (returnAt, nowMs = Date.now()) {
  const started = Number(returnAt)
  if (!Number.isFinite(started)) return true
  return nowMs - started >= RETURN_AWAITING_GRACE_MS
}

/**
 * 列表灌入后叠「待确认」：优先内存 prev，其次 session（刷新恢复）
 */
export function mergeDepartureWithReturnAwaiting (fromDevices, prev, awaitingSaved, inPlaceKeys) {
  const next = { ...(fromDevices || {}) }
  const prevMap = prev && typeof prev === 'object' ? prev : {}
  const savedMap = awaitingSaved && typeof awaitingSaved === 'object' ? awaitingSaved : {}
  const inPlaceMap = inPlaceKeys && typeof inPlaceKeys === 'object' ? inPlaceKeys : {}
  Object.keys(inPlaceMap).forEach((streamKey) => {
    if (next[streamKey]) delete next[streamKey]
  })

  Object.keys(savedMap).forEach((streamKey) => {
    const saved = savedMap[streamKey]
    if (!saved || typeof saved !== 'object') return
    const deviceDep = next[streamKey]
    if (!deviceDep) return
    if (inPlaceMap[streamKey]) return
    if (
      saved.id != null
      && deviceDep.id != null
      && String(saved.id) !== String(deviceDep.id)
    ) {
      return
    }
    if (prevMap[streamKey]?.status === 'return_awaiting' || prevMap[streamKey]?.status === 'return_pending') {
      return
    }
    if (isReturnAwaitingGraceExpired(saved.returnAt)) return
    next[streamKey] = {
      ...deviceDep,
      status: 'return_awaiting',
      priorStatus: saved.priorStatus || deviceDep.status,
      returnAt: saved.returnAt || Date.now(),
      autoReturn: false
    }
  })

  Object.keys(prevMap).forEach((streamKey) => {
    const local = prevMap[streamKey]
    if (!local) return
    if (inPlaceMap[streamKey]) return

    if (local.status === 'active' || local.status === 'pre_departed') {
      if (!fromDevices[streamKey]) {
        next[streamKey] = local
      } else if (local.departedAtFromSse && local.departedAt) {
        next[streamKey] = {
          ...fromDevices[streamKey],
          departedAt: local.departedAt,
          departedAtFromSse: true
        }
      }
      return
    }

    if (local.status !== 'return_awaiting' && local.status !== 'return_pending') return
    const deviceDep = fromDevices[streamKey]
    if (!deviceDep && local.status === 'return_pending') return
    if (!deviceDep && local.status === 'return_awaiting') return
    if (local.status === 'return_awaiting' && isReturnAwaitingGraceExpired(local.returnAt)) {
      const priorStatus = local.priorStatus === 'active' || local.priorStatus === 'pre_departed'
        ? local.priorStatus
        : 'active'
      next[streamKey] = {
        ...(deviceDep || local),
        status: priorStatus
      }
      if (local.departedAtFromSse && local.departedAt) {
        next[streamKey].departedAt = local.departedAt
        next[streamKey].departedAtFromSse = true
      }
      return
    }
    next[streamKey] = {
      ...(deviceDep || local),
      status: local.status,
      returnAt: local.returnAt,
      autoReturn: local.autoReturn,
      priorStatus: local.priorStatus
    }
    if (local.departedAtFromSse && local.departedAt) {
      next[streamKey].departedAt = local.departedAt
      next[streamKey].departedAtFromSse = true
    }
  })

  return next
}

function mapApiDepartureType (raw) {
  const s = String(raw == null ? '' : raw).trim()
  if (!s) return ''
  const upper = s.toUpperCase().replace(/-/g, '_')
  if (upper === 'NURSING' || upper === 'B') return DEPARTURE_NURSING
  if (upper === 'RETURN_ROOM' || upper === 'RETURNROOM' || upper === 'A') {
    return DEPARTURE_RETURN_ROOM
  }
  const lower = s.toLowerCase().replace(/-/g, '_')
  if (lower === 'nursing') return DEPARTURE_NURSING
  if (lower === 'return_room' || lower === 'returnroom') return DEPARTURE_RETURN_ROOM
  return ''
}

/** list 初始化：去向类型认 latestDepartureRecord.type */
function resolveDepartureTypeFromRecord (rec) {
  if (!rec || typeof rec !== 'object') return ''
  return mapApiDepartureType(rec.type)
}

function mapDepartureRecordStatus (raw) {
  const s = String(raw == null ? '' : raw).trim()
  if (!s) return ''
  const lower = s.toLowerCase().replace(/-/g, '_')
  if (lower === 'pre_departed') return 'pre_departed'
  if (lower === 'active' || lower === 'departed') return 'active'
  if (lower === 'return_pending') return 'return_pending'
  if (lower === 'return_awaiting') return 'return_awaiting'
  if (lower === 'completed' || lower === 'in_place') return ''
  return ''
}

function localDepartureFromSsePayload (payload) {
  if (!payload || typeof payload !== 'object') return null
  const rec = payload.departure && typeof payload.departure === 'object'
    ? payload.departure
    : null
  const status = mapDepartureRecordStatus(payload.status)
  if (!status) return null
  const id = (rec && rec.id != null && rec.id !== '')
    ? rec.id
    : payload.departureId
  const info = {
    nurseName: rec && rec.nurseName != null ? String(rec.nurseName) : '',
    status
  }
  const nested = payload.latestMonitoringMessage
  const raw = payload.messageTime != null && payload.messageTime !== ''
    ? payload.messageTime
    : (nested && typeof nested === 'object' && !Array.isArray(nested)
      ? nested.messageTime
      : undefined)
  const n = Number(raw)
  if (Number.isFinite(n) && n > 0) {
    info.departedAt = n < 1e12 ? n * 1000 : n
    info.departedAtFromSse = true
  }
  if (id != null && id !== '') info.id = id
  if (rec && rec.nurseId != null && rec.nurseId !== '') info.nurseId = rec.nurseId
  if (rec && rec.nurseAvatar != null) info.nurseAvatar = String(rec.nurseAvatar)
  return info
}

/** 离室 SSE 上的最新监控记录，与 list.latestMonitoringMessage 同一扁平结构 */
export function monitoringRecordOnDepartureSse (payload) {
  if (!payload || typeof payload !== 'object') return null
  const nested = payload.latestMonitoringMessage
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return null
  const record = {
    ...nested,
    deviceId: nested.deviceId ?? payload.deviceId,
    channelNo: nested.channelNo ?? payload.channelNo,
    alarmTime: payload.alarmTime
  }
  if (payload.messageTime != null && payload.messageTime !== '') {
    record.messageTime = payload.messageTime
  }
  return record
}

function bedStatusKey (device) {
  return String(device?.bedStatus == null ? '' : device.bedStatus)
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
}

/** 在位且无婴儿告警：要登记去向，不拿出 latestDepartureRecord */
function isAskRegisterFromList (device) {
  return device?.isAlarming === true
    && device?.hasBaby === false
    && bedStatusKey(device) === 'in_place'
}

/** 不在告警、床位也不是在位：显示 latestDepartureRecord */
function isShowDepartureFromList (device) {
  if (!device || device.isAlarming !== false) return false
  const bed = bedStatusKey(device)
  return bed !== '' && bed !== 'in_place'
}

function departureShownFromList (device) {
  const rec = device.latestDepartureRecord
  if (!rec || typeof rec !== 'object' || Array.isArray(rec)) return null
  let status = mapDepartureRecordStatus(device.bedStatus)
  if (!status) status = mapDepartureRecordStatus(rec.status)
  if (!status) status = 'active'
  let type = resolveDepartureTypeFromRecord(rec)
  if (!type && rec.subType != null && rec.subType !== '') type = DEPARTURE_NURSING
  if (!type) type = DEPARTURE_RETURN_ROOM
  const info = {
    id: rec.id != null && rec.id !== '' ? rec.id : undefined,
    type,
    status,
    nurseName: rec.nurseName != null ? String(rec.nurseName) : ''
  }
  if (rec.nurseId != null && rec.nurseId !== '') info.nurseId = rec.nurseId
  if (rec.nurseAvatar != null && String(rec.nurseAvatar) !== '') {
    info.nurseAvatar = String(rec.nurseAvatar)
  }
  if (type === DEPARTURE_NURSING && rec.subType != null && rec.subType !== '') {
    info.subType = rec.subType
  }
  const departedRaw = rec.departedAt
  const departedNum = Number(departedRaw)
  if (Number.isFinite(departedNum) && departedNum > 0) {
    info.departedAt = departedNum < 1e12 ? departedNum * 1000 : departedNum
  } else if (departedRaw != null && departedRaw !== '') {
    const t = Date.parse(String(departedRaw))
    if (!Number.isNaN(t) && t > 0) info.departedAt = t
  }
  return info
}

export function listAskRegisterStreamKeys (devices) {
  const keys = []
  if (!Array.isArray(devices)) return keys
  devices.forEach((device) => {
    if (!isAskRegisterFromList(device)) return
    const streamKey = buildMonitoringStreamKeyFromDevice(device.deviceSerial, device.channelNo)
    if (streamKey) keys.push(streamKey)
  })
  return keys
}

export function buildDepartureByStreamKeyFromDevices (devices) {
  const next = {}
  if (!Array.isArray(devices)) return next
  devices.forEach((device) => {
    if (!device) return
    const streamKey = buildMonitoringStreamKeyFromDevice(device.deviceSerial, device.channelNo)
    if (!streamKey) return
    if (isAskRegisterFromList(device)) return
    if (!isShowDepartureFromList(device)) return
    const info = departureShownFromList(device)
    if (!info) return
    next[streamKey] = info
  })
  return next
}

function coerceAlertLevelValue (raw) {
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return 0
  return n
}

function getMonitoringLevelValue (seeData) {
  if (!seeData || typeof seeData !== 'object') return 0
  const lv = seeData.level
  if (typeof lv === 'number' || typeof lv === 'string') {
    return coerceAlertLevelValue(lv)
  }
  if (lv && typeof lv === 'object' && !Array.isArray(lv)) {
    const fromObj = coerceAlertLevelValue(lv.value ?? lv.level)
    if (fromObj > 0) return fromObj
  }
  const fromTop = coerceAlertLevelValue(seeData.alarmLevel ?? seeData.alarm_level)
  if (fromTop > 0) return fromTop
  const bo = seeData.messageBO
  if (bo && typeof bo === 'object' && !Array.isArray(bo)) {
    return coerceAlertLevelValue(bo.level ?? bo.alarmLevel ?? bo.alarm_level)
  }
  return 0
}

function getMonitoringLevelMeta (seeData, fallbackLevelValue = 0) {
  const fromSee = getMonitoringLevelValue(seeData)
  const levelValue = fromSee > 0 ? fromSee : coerceAlertLevelValue(fallbackLevelValue)
  const levelCfg = levelValue && ALERT_LEVEL_CONFIG[levelValue] ? ALERT_LEVEL_CONFIG[levelValue] : null
  const lv = seeData?.level
  let levelText = ''
  if (lv && typeof lv === 'object' && !Array.isArray(lv)) {
    if (lv.desc != null && String(lv.desc).trim() !== '') {
      levelText = String(lv.desc).trim()
    } else if (lv.name != null && String(lv.name).trim() !== '') {
      levelText = String(lv.name).trim()
    }
  }
  if (!levelText && levelCfg) levelText = levelCfg.text
  return { levelValue, levelText, levelCfg }
}

export function readMessageTimeMs (record) {
  const n = Number(record?.messageTime)
  if (!Number.isFinite(n) || n <= 0) return 0
  return n < 1e12 ? n * 1000 : n
}

/**
 * 仅 list 首屏条缺等级时用 device.alarmLevel 补色。
 * SSE 场景禁止再拿 list 等级补洞。
 */
export function enrichSurveillanceAlarmLevel (sceneEntry, device) {
  if (!sceneEntry) return sceneEntry
  const allowListFallback = sceneEntry.fromListInit === true
  const fallback = allowListFallback ? (Number(device?.alarmLevel) || 0) : 0
  const { levelValue, levelText, levelCfg } = getMonitoringLevelMeta(sceneEntry.seeData, fallback)
  const listLevelText = allowListFallback && device?.alarmLevelText != null
    ? String(device.alarmLevelText).trim()
    : ''
  const nextText = levelText || listLevelText || sceneEntry.levelText || ''
  const nextCfg = levelCfg || sceneEntry.levelCfg || null
  const nextValue = levelValue > 0 ? levelValue : (sceneEntry.levelValue || 0)
  if (sceneEntry.levelValue === nextValue
    && sceneEntry.levelText === nextText
    && sceneEntry.levelCfg === nextCfg) {
    return sceneEntry
  }
  return {
    ...sceneEntry,
    levelValue: nextValue,
    levelText: nextText,
    levelCfg: nextCfg
  }
}

export function buildListInitSceneEntry (device) {
  if (!device || device.needInference !== true) return null
  const hasBaby = device.hasBaby
  const isAlarming = device.isAlarming === true
  if (typeof hasBaby !== 'boolean' && !isAlarming) return null

  const effectiveHasBaby = typeof hasBaby === 'boolean' ? hasBaby : false
  const thumb = (device.latestMonitoringImageThumbnail || '').trim()
  const full = (device.latestMonitoringImage || '').trim()
  const levelRaw = Number(device.alarmLevel)
  const levelValue = Number.isFinite(levelRaw) && levelRaw > 0 ? levelRaw : 0
  const levelCfg = ALERT_LEVEL_CONFIG[levelValue] || null
  const listLevelText = device.alarmLevelText != null ? String(device.alarmLevelText).trim() : ''
  const levelText = listLevelText || levelCfg?.text || ''
  const eventTimeMs = Number(device.alarmTimeMs) || 0
  const listSummary = device.alarmSummary != null ? String(device.alarmSummary).trim() : ''
  const summary = listSummary
    || (effectiveHasBaby === false ? '无婴儿' : (isAlarming ? '监控预警' : '安全'))
  const seeData = {
    deviceId: device.deviceSerial,
    channelNo: device.channelNo,
    hasBaby: effectiveHasBaby,
    isViolation: isAlarming || undefined,
    alarmLevel: levelValue > 0 ? levelValue : undefined,
    level: { value: levelValue, desc: levelText || undefined },
    imageUrls: full ? [full] : (thumb ? [thumb] : []),
    thumbnailUrls: thumb ? [thumb] : [],
    summary
  }
  const keepAlarmMeta = isAlarming || effectiveHasBaby === false || levelValue > 0
  return {
    eventTimeMs,
    levelValue: keepAlarmMeta ? levelValue : 0,
    levelText: keepAlarmMeta ? levelText : '',
    levelCfg: keepAlarmMeta ? levelCfg : null,
    thumb: thumb || full,
    fullImage: full || thumb,
    recordId: undefined,
    seeData,
    ticketClosed: false,
    fromListInit: true
  }
}

function buildSurveillanceSceneEntry (item, { ticketClosed } = {}) {
  const seeData = getSeeData(item)
  const { hasBaby, isViolation } = getMonitoringSceneFlags(seeData)
  const eventTimeMs = readMessageTimeMs(seeData)
  const { levelValue, levelText, levelCfg } = getMonitoringLevelMeta(seeData)
  const clearLevel = hasBaby === true && isViolation !== true
  return {
    eventTimeMs,
    levelValue: clearLevel ? 0 : levelValue,
    levelText: clearLevel ? '' : levelText,
    levelCfg: clearLevel ? null : levelCfg,
    thumb: getMonitoringThumbnailUrl(seeData),
    fullImage: getMonitoringFullImageUrl(seeData),
    recordId: getMonitoringAlarmRecordId(seeData, item),
    seeData,
    ticketClosed: ticketClosed === true,
    fromListInit: false
  }
}

function writeSseScene (data, item) {
  const sk = getStreamKey(item)
  if (!sk || sk.indexOf('_unknown') === 0) return
  delete data.processedKeys[sk]
  const ticketClosed = getMonitoringIsProcessed(item) === true
  if (ticketClosed) data.processedKeys[sk] = Date.now()
  data.sceneByStreamKey[sk] = buildSurveillanceSceneEntry(item, { ticketClosed })
  data.sseSceneKeys[sk] = true
}

/**
 * 用当前 monitoring_alarm 列表铺 SSE 场景，再把还没有 SSE 的路用设备行补上。
 * 列表新在前，同路只留第一条。
 */
export function seedScenesFromAlarmList (data, alarmList, processedKeys) {
  data.processedKeys = processedKeys && typeof processedKeys === 'object' ? { ...processedKeys } : {}
  data.sseSceneKeys = {}
  data.sceneByStreamKey = {}
  const list = Array.isArray(alarmList) ? alarmList : []
  for (let i = 0; i < list.length; i += 1) {
    const item = list[i]
    if (!item) continue
    const sk = getStreamKey(item)
    if (!sk || sk.indexOf('_unknown') === 0 || data.sseSceneKeys[sk]) continue
    const ticketClosed = Boolean(data.processedKeys[sk]) || getMonitoringIsProcessed(item) === true
    data.sceneByStreamKey[sk] = buildSurveillanceSceneEntry(item, { ticketClosed })
    data.sseSceneKeys[sk] = true
  }
  rebuildScenesFromDevices(data)
}

/** list 只填还没有 SSE 场景的路 */
export function rebuildScenesFromDevices (data) {
  const next = {}
  const sseKeys = data.sseSceneKeys || {}
  const prev = data.sceneByStreamKey || {}
  Object.keys(sseKeys).forEach((sk) => {
    if (prev[sk]) next[sk] = prev[sk]
  })
  const list = Array.isArray(data.devices) ? data.devices : []
  for (let i = 0; i < list.length; i += 1) {
    const device = list[i]
    const sk = buildMonitoringStreamKeyFromDevice(device?.deviceSerial, device?.channelNo)
    if (!sk || next[sk]) continue
    const entry = buildListInitSceneEntry(device)
    if (!entry) continue
    if (data.processedKeys && data.processedKeys[sk]) entry.ticketClosed = true
    next[sk] = entry
  }
  data.sceneByStreamKey = next
}

/**
 * 一条离室 SSE 落到 draft.data。
 * 事件看 payload.status：in_place 回在位，pre_departed 预登记，departed 已离开，return_pending 返回待确认。
 * payload.departure 有值才更新登记去向。departure_timeout_warning 不改数据。departure_matched 不改场景。
 */
export function applyDepartureSseEvent (data, streamKey, evt) {
  if (!data || !streamKey || !evt) return
  if (evt.eventType === 'departure_timeout_warning') return
  const payload = evt.payload && typeof evt.payload === 'object' ? evt.payload : {}
  const bed = String(payload.status == null ? '' : payload.status).trim().toLowerCase().replace(/-/g, '_')
  const skipScene = evt.eventType === 'departure_matched' || bed === 'departure_matched'
  const monitoringRecord = skipScene ? null : monitoringRecordOnDepartureSse(payload)

  if (bed === 'in_place' || bed === 'completed') {
    delete data.departureByStreamKey[streamKey]
    data.inPlaceKeys[streamKey] = true
  } else if (bed === 'pre_departed' || bed === 'departed' || bed === 'active' || bed === 'return_pending') {
    const fromSse = localDepartureFromSsePayload(payload)
    if (fromSse) {
      const cur = data.departureByStreamKey[streamKey]
      const nextRow = {
        ...(cur || {}),
        ...fromSse,
        nurseName: fromSse.nurseName || (cur && cur.nurseName) || '',
        nurseId: fromSse.nurseId || (cur && cur.nurseId),
        nurseAvatar: fromSse.nurseAvatar || (cur && cur.nurseAvatar)
      }
      const dep = payload.departure && typeof payload.departure === 'object'
        ? payload.departure
        : null
      const depType = mapApiDepartureType(dep && dep.type)
      if (depType === DEPARTURE_RETURN_ROOM) {
        nextRow.type = DEPARTURE_RETURN_ROOM
        delete nextRow.subType
      } else if (depType === DEPARTURE_NURSING) {
        nextRow.type = DEPARTURE_NURSING
        if (dep && dep.subType != null && dep.subType !== '') {
          nextRow.subType = dep.subType
        } else {
          delete nextRow.subType
        }
      }
      data.departureByStreamKey[streamKey] = nextRow
    }
  }

  if (!monitoringRecord) return
  writeSseScene(data, {
    type: 'monitoring_alarm',
    data: monitoringRecord
  })
}

export function applyMonitoringAlarmMessage (data, msg) {
  if (!data || !msg) return
  writeSseScene(data, msg)
}
